import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Button } from "@/components/kit";
import PayDues from "@/components/PayDues";

const DUES_LABEL = { active: "Active", waived: "Waived", simulated: "Pending (simulated)", free: "Free", manual: "Manual", unpaid: "Unpaid", paid: "Paid" };
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");
const methodLabel = (m) => ({ zelle: "Zelle", cash: "Cash", check: "Check", card: "Card", square: "Square", other: "Other" }[m] || (m || "Payment"));

export default function PaymentHistory() {
  const [member, setMember] = useState(undefined);
  const [paying, setPaying] = useState(false);
  const reload = () => api.get("/membership/me").then((r) => setMember(r.data.member)).catch(() => setMember(null));
  useEffect(() => { reload(); }, []);

  if (member === undefined) return <div className="text-ink/60 py-8">Loading…</div>;
  if (member === null) return <div className="text-ink/60 py-8" data-testid="payments-empty">No membership record found on your account.</div>;

  const dues = member.dues || {};
  const payments = [...(member.manual_payments || [])].sort((a, b) => (b.logged_at || "").localeCompare(a.logged_at || ""));

  return (
    <div data-testid="payment-history-page">
      <h1 className="font-zilla text-3xl font-semibold mb-1">Payment history</h1>
      <p className="text-[16px] text-ink/70 mb-5">Everything you've paid toward your Rosemoor membership.</p>

      <div className="rounded-2xl p-5 bg-ink text-cream mb-5" data-testid="dues-summary">
        <div className="text-[12px] uppercase tracking-widest text-gold font-bold">Current dues</div>
        <div className="font-zilla text-2xl font-semibold mt-1">{DUES_LABEL[dues.status] || "—"}</div>
        <div className="text-cream/75 text-[14px] mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {dues.cadence && dues.cadence !== "none" && <span className="capitalize">{dues.cadence} billing</span>}
          {dues.amount_cents ? <span>{money(dues.amount_cents)} / period</span> : null}
          {dues.waived_reason && <span>Reason: {dues.waived_reason}</span>}
          {dues.renews_on && <span>Renews {fmt(dues.renews_on)}</span>}
        </div>
        {!["waived", "free"].includes(dues.status) && (
          <Button data-testid="pay-dues-btn" className="mt-4" onClick={() => setPaying(true)}>
            <Icon name="CreditCard" className="w-4 h-4" /> Pay dues
          </Button>
        )}
      </div>

      {paying && <PayDues amountCents={dues.amount_cents || 0} interval={dues.cadence === "monthly" ? "monthly" : "yearly"} onClose={() => setPaying(false)} onPaid={() => { setPaying(false); reload(); }} />}

      <h2 className="font-zilla text-xl font-semibold mb-2">Payments received</h2>
      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="payments-list">
        {payments.length === 0 && (
          <div className="p-10 text-center text-ink/55" data-testid="payments-none">No payments have been logged yet.</div>
        )}
        {payments.map((p) => (
          <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-center gap-3 px-5 py-4 border-b border-ink/8 last:border-0">
            <div className="w-10 h-10 rounded-full bg-olive/15 flex items-center justify-center shrink-0">
              <Icon name="Receipt" className="w-5 h-5 text-dark-olive" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[16px] font-semibold">{money(p.amount_cents)}</div>
              <div className="text-[13px] text-ink/55">{methodLabel(p.method)}{p.note ? ` · ${p.note}` : ""}</div>
            </div>
            <div className="text-[13px] text-ink/60 shrink-0">{fmt(p.logged_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
